import { cn } from "./cn";

/**
 * IsoWorld — WO#5 Neon-Editorial v2.
 *
 * SVG isometric "circuit-city" hero illustration.
 * Composition mirrors Claudio's reference:
 *   - Isometric grid of small building blocks (teal-tinted)
 *   - Central coral nucleus (Grid Zero) — 3-tier structure with
 *     strong coral emission and vertical light beam
 *   - Peripheral vertical light beams (teal + coral) rising above
 *     specific towers
 *   - Etched grid deck beneath everything
 *   - Ambient particles
 *
 * Zero rasters, zero JS animation, all purely SVG. The callouts
 * (leader lines + labels) are DOM overlays composed on top of
 * this canvas by the Hero section — see NarrativePage.
 */
export function IsoWorld({ className }: { className?: string }) {
  return (
    <div
      data-testid="iso-world"
      aria-hidden
      className={cn("relative w-full h-full select-none", className)}
    >
      <svg
        viewBox="0 0 1000 640"
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 h-full w-full"
      >
        <defs>
          {/* Isometric transform: rotate 45° then scale-y 0.5 approximated
              by 30° isometric via matrix (cos30 sin30 -cos30 sin30 dx dy). */}
          <linearGradient id="deckFade" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0" stopColor="#0A0F14" stopOpacity="0" />
            <stop offset="40%" stopColor="#0A0F14" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#0A0F14" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id="blockTop" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#132A3A" />
            <stop offset="1" stopColor="#0B1A25" />
          </linearGradient>
          <linearGradient id="blockLeft" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0F2230" />
            <stop offset="1" stopColor="#08141C" />
          </linearGradient>
          <linearGradient id="blockRight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0A1A24" />
            <stop offset="1" stopColor="#06111A" />
          </linearGradient>
          <linearGradient id="tealEdge" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#2DB6F0" stopOpacity="0" />
            <stop offset="0.5" stopColor="#2DB6F0" stopOpacity="0.9" />
            <stop offset="1" stopColor="#2DB6F0" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="coralGrad" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0" stopColor="#FF6B76" />
            <stop offset="1" stopColor="#FF3040" />
          </linearGradient>
          <radialGradient id="coralGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor="#FF4757" stopOpacity="0.85" />
            <stop offset="0.5" stopColor="#FF4757" stopOpacity="0.25" />
            <stop offset="1" stopColor="#FF4757" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="tealGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor="#2DB6F0" stopOpacity="0.75" />
            <stop offset="1" stopColor="#2DB6F0" stopOpacity="0" />
          </radialGradient>
          <filter id="softBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* Etched deck (isometric floor) */}
        <g opacity="0.65" transform="translate(500 360)">
          {/* diagonal lines forming an isometric floor plate */}
          {Array.from({ length: 22 }).map((_, i) => {
            const off = (i - 11) * 42;
            return (
              <g key={`d-${i}`}>
                <line
                  x1={off}
                  y1={-160}
                  x2={off + 260}
                  y2={-30}
                  stroke="#2DB6F0"
                  strokeOpacity={0.09}
                  strokeWidth={1}
                />
                <line
                  x1={off}
                  y1={160}
                  x2={off - 260}
                  y2={30}
                  stroke="#2DB6F0"
                  strokeOpacity={0.09}
                  strokeWidth={1}
                />
              </g>
            );
          })}
        </g>

        {/* Peripheral vertical light beams */}
        <BeamsLayer />

        {/* Isometric block field */}
        <IsoField />

        {/* Nucleus (Grid Zero) — coral tower */}
        <NucleusTower />

        {/* Ambient particles (teal + coral) */}
        <g>
          {SPARKLES.map((p, i) => (
            <circle
              key={`s-${i}`}
              cx={p.x}
              cy={p.y}
              r={p.r}
              fill={p.color}
              opacity={p.o}
            />
          ))}
        </g>

        {/* Bottom deck fade */}
        <rect x="0" y="380" width="1000" height="260" fill="url(#deckFade)" />
      </svg>
    </div>
  );
}

/* ─── Isometric block field ───────────────────────────────────
   Grid of small block towers arranged in an isometric plane
   around the center. Heights vary — a mix of tall accent
   structures and short data pads. */
function IsoField() {
  const cells: { gx: number; gy: number; h: number; accent?: "teal" }[] = [];
  // Grid range around center. Skip cells that overlap the nucleus.
  for (let gx = -6; gx <= 6; gx++) {
    for (let gy = -6; gy <= 6; gy++) {
      if (Math.abs(gx) <= 1 && Math.abs(gy) <= 1) continue; // reserved for nucleus
      // Deterministic pseudo-random height per cell
      const seed = (gx * 73856093) ^ (gy * 19349663);
      const rand = ((seed >>> 0) % 1000) / 1000;
      // Skip a portion of cells → visual breathing room
      if (rand < 0.28) continue;
      const h = 8 + rand * 42; // 8..50
      const accent = rand > 0.9 ? "teal" : undefined;
      cells.push({ gx, gy, h, accent });
    }
  }
  return (
    <g>
      {cells
        .sort((a, b) => (a.gx + a.gy) - (b.gx + b.gy)) // paint back-to-front
        .map((c, i) => (
          <IsoBlock key={i} gx={c.gx} gy={c.gy} h={c.h} accent={c.accent} />
        ))}
    </g>
  );
}

/**
 * Draws one isometric block at grid coord (gx, gy) with height `h`.
 * Isometric projection: x' = (gx - gy) * 36; y' = (gx + gy) * 20.
 * Origin translated so the center of the field sits at (500, 380).
 */
function IsoBlock({
  gx,
  gy,
  h,
  accent,
}: {
  gx: number;
  gy: number;
  h: number;
  accent?: "teal";
}) {
  const cx = 500 + (gx - gy) * 36;
  const cy = 380 + (gx + gy) * 20 - h;
  const w = 32; // half-width of tile
  const d = 18; // half-depth of tile
  // Top rhombus: N, E, S, W in screen coords
  const N = [cx, cy - d];
  const E = [cx + w, cy];
  const S = [cx, cy + d];
  const W = [cx - w, cy];
  // Left face: W, S bottom, W bottom
  const Sb = [cx, cy + d + h];
  const Wb = [cx - w, cy + h];
  const Eb = [cx + w, cy + h];
  return (
    <g>
      {/* Left face */}
      <polygon
        points={`${W[0]},${W[1]} ${S[0]},${S[1]} ${Sb[0]},${Sb[1]} ${Wb[0]},${Wb[1]}`}
        fill="url(#blockLeft)"
      />
      {/* Right face */}
      <polygon
        points={`${S[0]},${S[1]} ${E[0]},${E[1]} ${Eb[0]},${Eb[1]} ${Sb[0]},${Sb[1]}`}
        fill="url(#blockRight)"
      />
      {/* Top */}
      <polygon
        points={`${N[0]},${N[1]} ${E[0]},${E[1]} ${S[0]},${S[1]} ${W[0]},${W[1]}`}
        fill="url(#blockTop)"
        stroke={accent === "teal" ? "#2DB6F0" : "#183040"}
        strokeOpacity={accent === "teal" ? 0.8 : 0.5}
        strokeWidth={0.75}
      />
      {/* Optional teal edge highlight on the top-right ridge */}
      {accent === "teal" && (
        <line
          x1={S[0]}
          y1={S[1]}
          x2={E[0]}
          y2={E[1]}
          stroke="#2DB6F0"
          strokeOpacity={0.9}
          strokeWidth={1}
        />
      )}
      {/* Faint window rows on the left face (only for tall blocks) */}
      {h > 24 && (
        <g fill="#2DB6F0" opacity={0.6}>
          {Array.from({ length: Math.floor((h - 6) / 8) }).map((_, i) => (
            <rect
              key={i}
              x={cx - w + 6}
              y={cy + 4 + i * 8}
              width={w - 12}
              height={2}
              opacity={0.14 + (i % 2) * 0.10}
            />
          ))}
        </g>
      )}
    </g>
  );
}

function NucleusTower() {
  const cx = 500;
  const cy = 380;
  return (
    <g>
      {/* Ambient coral halo behind the tower */}
      <ellipse
        cx={cx}
        cy={cy + 30}
        rx={210}
        ry={90}
        fill="url(#coralGlow)"
        opacity={0.75}
      />
      <ellipse
        cx={cx}
        cy={cy}
        rx={130}
        ry={54}
        fill="url(#coralGlow)"
        opacity={0.9}
        filter="url(#softBlur)"
      />

      {/* 3-tier tower — bottom widest, top narrowest */}
      {[
        { size: 60, depth: 34, h: 62, y: cy, fill: "#B02030" },
        { size: 44, depth: 24, h: 58, y: cy - 60, fill: "#D0303C" },
        { size: 30, depth: 16, h: 62, y: cy - 118, fill: "#FF4757" },
      ].map((t, i) => {
        const N = [cx, t.y - t.depth];
        const E = [cx + t.size, t.y];
        const S = [cx, t.y + t.depth];
        const W = [cx - t.size, t.y];
        const Wb = [cx - t.size, t.y + t.h];
        const Sb = [cx, t.y + t.depth + t.h];
        const Eb = [cx + t.size, t.y + t.h];
        return (
          <g key={i}>
            <polygon
              points={`${W[0]},${W[1]} ${S[0]},${S[1]} ${Sb[0]},${Sb[1]} ${Wb[0]},${Wb[1]}`}
              fill={t.fill}
              opacity={0.72}
            />
            <polygon
              points={`${S[0]},${S[1]} ${E[0]},${E[1]} ${Eb[0]},${Eb[1]} ${Sb[0]},${Sb[1]}`}
              fill={t.fill}
              opacity={0.55}
            />
            <polygon
              points={`${N[0]},${N[1]} ${E[0]},${E[1]} ${S[0]},${S[1]} ${W[0]},${W[1]}`}
              fill="url(#coralGrad)"
              stroke="#FF6B76"
              strokeOpacity={0.9}
              strokeWidth={1.2}
            />
          </g>
        );
      })}

      {/* Central vertical beam rising from the top */}
      <rect
        x={cx - 2}
        y={20}
        width={4}
        height={230}
        fill="#FF4757"
        opacity={0.8}
      />
      <rect
        x={cx - 6}
        y={20}
        width={12}
        height={230}
        fill="#FF4757"
        opacity={0.20}
        filter="url(#softBlur)"
      />

      {/* Ground contact rings */}
      {[0, 1, 2].map((i) => (
        <ellipse
          key={i}
          cx={cx}
          cy={cy + 60 + i * 8}
          rx={90 - i * 20}
          ry={20 - i * 4}
          fill="none"
          stroke="#FF4757"
          strokeOpacity={0.32 - i * 0.08}
          strokeWidth={0.75}
        />
      ))}
    </g>
  );
}

function BeamsLayer() {
  const beams: { x: number; color: string; alpha: number; h: number }[] = [
    { x: 260, color: "#2DB6F0", alpha: 0.55, h: 220 },
    { x: 340, color: "#2DB6F0", alpha: 0.35, h: 190 },
    { x: 640, color: "#2DB6F0", alpha: 0.55, h: 210 },
    { x: 720, color: "#2DB6F0", alpha: 0.35, h: 180 },
    { x: 780, color: "#FF4757", alpha: 0.5, h: 200 },
    { x: 210, color: "#FF4757", alpha: 0.45, h: 220 },
  ];
  return (
    <g>
      {beams.map((b, i) => (
        <g key={i}>
          <rect
            x={b.x - 1}
            y={30}
            width={2}
            height={b.h}
            fill={b.color}
            opacity={b.alpha}
          />
          <rect
            x={b.x - 6}
            y={30}
            width={12}
            height={b.h}
            fill={b.color}
            opacity={b.alpha * 0.28}
            filter="url(#softBlur)"
          />
        </g>
      ))}
    </g>
  );
}

const SPARKLES: { x: number; y: number; r: number; color: string; o: number }[] = [
  { x: 120, y: 130, r: 1.2, color: "#2DB6F0", o: 0.85 },
  { x: 260, y: 90, r: 1.0, color: "#2DB6F0", o: 0.7 },
  { x: 420, y: 60, r: 1.5, color: "#74D4FF", o: 0.9 },
  { x: 580, y: 80, r: 1.0, color: "#2DB6F0", o: 0.75 },
  { x: 720, y: 120, r: 1.2, color: "#FF4757", o: 0.65 },
  { x: 860, y: 90, r: 1.0, color: "#2DB6F0", o: 0.7 },
  { x: 940, y: 170, r: 1.4, color: "#FF4757", o: 0.55 },
  { x: 60, y: 240, r: 1.0, color: "#2DB6F0", o: 0.6 },
  { x: 200, y: 300, r: 1.2, color: "#FF4757", o: 0.4 },
  { x: 380, y: 200, r: 1.0, color: "#2DB6F0", o: 0.65 },
  { x: 580, y: 260, r: 1.5, color: "#74D4FF", o: 0.7 },
  { x: 800, y: 280, r: 1.0, color: "#2DB6F0", o: 0.6 },
  { x: 900, y: 340, r: 1.2, color: "#FF4757", o: 0.35 },
];
